/**
 * Silero VAD Provider
 *
 * Voice Activity Detection using Silero VAD model via @ricky0123/vad-web.
 * MIT license, fully commercial-safe.
 *
 * Silero VAD is highly accurate and works at 16kHz sample rate.
 */

import { VADProvider } from './VADProvider.js';
import { MicVAD } from '@ricky0123/vad-web';

export class SileroVADProvider extends VADProvider {
  constructor() {
    super('silero');
    this.vad = null;
    this.stream = null;
    this.initialized = false;
    this.isSpeaking = false;

    // Speech audio buffer (collected during speech)
    this.speechAudio = null;

    // Options - lowered thresholds for better speech detection
    this.options = {
      positiveSpeechThreshold: 0.3,  // Lower = more sensitive to speech (default 0.5)
      negativeSpeechThreshold: 0.15, // Lower = needs clearer silence to end (default 0.35)
      redemptionMs: 800,      // Wait 800ms after potential speech end (was 500)
      preSpeechPadMs: 500,    // Include 500ms before speech start (was 300)
      minSpeechMs: 150,       // Minimum 150ms to count as speech (was 250)
      model: 'v5',            // Use v5 model (more accurate)
    };
  }

  /**
   * Check if Silero VAD is supported on this device
   * Requires WASM SIMD for ONNX runtime
   */
  static isSupported(capabilities) {
    return capabilities.wasmSimd;
  }

  /**
   * Initialize Silero VAD with a MediaStream
   * @param {Object} options - Configuration options
   * @param {MediaStream} options.stream - Audio stream to process (optional if getStream provided)
   * @param {Function} options.getStream - Function that returns a MediaStream (alternative to stream)
   */
  async initialize(options = {}) {
    if (this.initialized) {
      await this.destroy();
    }

    console.log('[SileroVAD] Initializing...');

    try {
      // Merge options
      const vadOptions = {
        ...this.options,
        ...options,
        // Start automatically when initialized
        startOnLoad: false,
        // Use AudioWorklet for better performance
        processorType: 'auto',
        // Asset paths for ONNX models and worklet
        baseAssetPath: '/vad/',
        onnxWASMBasePath: '/assets/',
      };

      // Handle stream provision
      if (options.stream) {
        this.stream = options.stream;
        vadOptions.getStream = async () => this.stream;
      } else if (options.getStream) {
        vadOptions.getStream = options.getStream;
      } else {
        // Default: request microphone access
        vadOptions.getStream = async () => {
          const stream = await navigator.mediaDevices.getUserMedia({
            audio: {
              echoCancellation: true,
              noiseSuppression: false, // We handle noise suppression separately
              autoGainControl: true,
            }
          });
          this.stream = stream;
          return stream;
        };
      }

      // Pause stream (we manage pausing ourselves)
      vadOptions.pauseStream = async (stream) => {
        // Don't actually pause the stream, we'll handle this
      };

      // Resume stream
      vadOptions.resumeStream = async (stream) => {
        return stream;
      };

      // Speech callbacks
      vadOptions.onSpeechStart = () => {
        console.log('[SileroVAD] Speech start detected');
        this.isSpeaking = true;
        this.speechAudio = null; // Reset buffer
        if (this.onSpeechStart) {
          this.onSpeechStart();
        }
      };

      vadOptions.onSpeechEnd = (audio) => {
        console.log('[SileroVAD] Speech end detected, audio length:', audio?.length);
        this.isSpeaking = false;
        this.speechAudio = audio; // Store the speech audio
        if (this.onSpeechEnd) {
          this.onSpeechEnd(audio);
        }
      };

      vadOptions.onVADMisfire = () => {
        console.log('[SileroVAD] VAD misfire (speech too short)');
        this.isSpeaking = false;
        if (this.onVADMisfire) {
          this.onVADMisfire();
        }
      };

      vadOptions.onSpeechRealStart = () => {
        console.log('[SileroVAD] Speech confirmed (real start)');
      };

      // Create the VAD instance
      this.vad = await MicVAD.new(vadOptions);

      this.initialized = true;
      console.log('[SileroVAD] Initialized successfully');
    } catch (error) {
      console.error('[SileroVAD] Initialization failed:', error);
      throw error;
    }
  }

  /**
   * Start VAD processing
   */
  async start() {
    if (!this.initialized) {
      throw new Error('SileroVAD not initialized. Call initialize() first.');
    }

    console.log('[SileroVAD] Starting...');
    try {
      await this.vad.start();
      console.log('[SileroVAD] Started successfully, listening for speech...');
    } catch (error) {
      console.error('[SileroVAD] Failed to start:', error);
      throw error;
    }
  }

  /**
   * Stop/pause VAD processing
   */
  async stop() {
    if (this.vad) {
      console.log('[SileroVAD] Pausing...');
      await this.vad.pause();
    }
  }

  /**
   * Process audio frame (not used with Silero VAD - it processes internally)
   * This method is kept for interface compatibility.
   *
   * Note: Silero VAD via MicVAD processes the stream internally.
   * For manual frame processing, you would need to use FrameProcessor directly.
   *
   * @param {Float32Array} samples - Audio samples (not used)
   * @param {number} sampleRate - Sample rate (not used)
   * @returns {boolean} - Current speech state
   */
  process(samples, sampleRate) {
    // Silero VAD processes audio internally via MicVAD
    // This just returns the current speech state
    return this.isSpeaking;
  }

  /**
   * Get the last detected speech audio segment
   * @returns {Float32Array|null} - Speech audio at 16kHz
   */
  getSpeechAudio() {
    return this.speechAudio;
  }

  /**
   * Clear the speech audio buffer
   */
  clearSpeechAudio() {
    this.speechAudio = null;
  }

  /**
   * Update VAD options
   * @param {Object} options - New options to merge
   */
  setOptions(options) {
    if (this.vad) {
      this.vad.setOptions(options);
    }
    Object.assign(this.options, options);
  }

  /**
   * Clean up resources
   */
  async destroy() {
    console.log('[SileroVAD] Destroying...');

    try {
      if (this.vad) {
        await this.vad.destroy();
        this.vad = null;
      }

      this.stream = null;
      this.speechAudio = null;
      this.isSpeaking = false;
      this.initialized = false;

      console.log('[SileroVAD] Destroyed successfully');
    } catch (error) {
      console.error('[SileroVAD] Destroy error:', error);
    }
  }
}

export default SileroVADProvider;
